import { Box, Button } from '@amzn/awsui-components-react';
import { createRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Timeline from 'react-vis-timeline-2';
import { TimelineGroup, TimelineItem } from 'vis-timeline';

import { constants } from '../../../common/Localization';
import { TIMELINE_MAX_HEIGHT, TIMELINE_MOVEMENT_PERCENTAGE, TIMELINE_ZOOM_MAX, TIMELINE_ZOOM_MIN } from './constants';
interface Props {
    items: TimelineItem[],
    setItems: (items: TimelineItem[]) => void;
    groups: TimelineGroup[],
    first: () => string;
    current: () => string;
    onAdd: (groupIds: string[], waveId: string) => Promise<void>;
    onRemove: (groupIds: string[]) => Promise<void>;
}

// The new vis-timeline library doesn't define type correctly; we need the value field to display group name
type TimelineItemCompatible = TimelineItem & { value?: string };

interface BackgroundIdMap {
    [id: string]: TimelineItemCompatible;
}

function VisTimeline({groups, items, first, current, onAdd, onRemove, setItems}: Props) {
    const timelineRef = createRef<Timeline>();
    // backgroundIdMap is a way to look up corresponding wave information, given the destination waveId
    const [ backgroundIdMap, setBackgroundIdMap ] = useState<BackgroundIdMap>({});
    useEffect(() => {
        if (!groups?.length) {
            return;
        }
        const newBackgroundIdMap: BackgroundIdMap = {};
        items.filter(item => item.type === 'background').forEach(item => {
            newBackgroundIdMap[item.id] = item;
        });
        setBackgroundIdMap(newBackgroundIdMap);
    }, [ groups, items ]);

    /**
     * Move the timeline a given percentage to left or right
     * @param {Number} percentage, for example 0.1 (left) or -0.1 (right)
     */
    function move(percentage: number) {
        const timeline = timelineRef?.current?.timeline;
        const range = timeline?.getWindow();
        if (!timeline || !range) {
            return;
        }
        const intervalInMilliSec = range.end.getTime() - range.start.getTime();
        timeline.setWindow(range.start.valueOf() - intervalInMilliSec * percentage, range.end.valueOf() - intervalInMilliSec * percentage);
    }

    const zoomIn = () => timelineRef?.current?.timeline?.zoomIn(TIMELINE_MOVEMENT_PERCENTAGE);
    const zoomOut = () => timelineRef?.current?.timeline?.zoomOut(TIMELINE_MOVEMENT_PERCENTAGE);
    const moveLeft = () => move(TIMELINE_MOVEMENT_PERCENTAGE);
    const moveRight = () => move(-TIMELINE_MOVEMENT_PERCENTAGE);
    const moveToFirst = () => timelineRef?.current?.timeline?.focus(first());
    const moveToCurrent = () => {
        const currentGroupId: string = current();
        const timeline = timelineRef?.current?.timeline;
        if (timeline && currentGroupId) {
            return timeline.focus(currentGroupId);
        }
        timeline?.moveTo(new Date());
    };
    const options = {
        locale: 'en',
        zoomMin: TIMELINE_ZOOM_MIN,
        zoomMax: TIMELINE_ZOOM_MAX,
        groupOrder: 'value',
        verticalScroll: true,
        horizontalScroll: true,
        maxHeight: TIMELINE_MAX_HEIGHT,
        tooltip: {
            followMouse: true
        },
        onInitialDrawComplete: moveToFirst,
        editable: {
            add: true,
            updateGroup: true,
            remove: true
        },
        onAdd: function (item: TimelineItemCompatible, callback: (item: TimelineItem) => void) {
            // stop adding new item if user double-clicked on the timeline
            if(!item.type && item.content === 'new item') {
                return;
            }
            // continue to add item if user dragged and dropped the item
            const targetBackground = backgroundIdMap['bg-' + item.group];
            if (!targetBackground) {
                return;
            }
            item.start = targetBackground.start;
            item.end = targetBackground.end;
            item.value = item.content;
            item.content = !!targetBackground.value ? item.content + ' - ' + targetBackground.value : item.content;
            item.title = item.content;
            onAdd([`${item.id}`], `${item.group}`);

            const existingItemIndex = items.findIndex(timelineItem => timelineItem.id === item.id);
            if (existingItemIndex >= 0) {
                items[existingItemIndex] = item;
                setItems(items);
            } else {
                callback(item);
            }
        },
        onRemove: function(item: TimelineItem, callback: (item: TimelineItem) => void) {
            onRemove([`${item.id}`]);
            callback(item);
        },
        onMove: function(item: TimelineItemCompatible, callback: (item: TimelineItem) => void) {
            const targetBackground = backgroundIdMap['bg-' + item.group];
            if (!targetBackground) {
                return;
            }
            item.start = targetBackground.start;
            item.end = targetBackground.end;
            item.content = !!targetBackground.value ? item.value + ' - ' + targetBackground.value : item.value || '';
            item.title = item.content;
            onAdd([`${item.id}`], `${item.group}`);
            callback(item);
        },
        // TODO: itemData is not typed correctly in the @types library, casting to any for now
        template: function(itemData: any, element: Element) {
            if (itemData.props != null && itemData.element != null) {
                return;
            }
            const fields = itemData.content.split(',');
            const name = fields[0];
            const pct = fields[1] || '0%';
            const instance = (
                <div>
                    <div>{name}</div>
                    <div className='group-progress-wrapper'>
                        <div className='progress' style={{width: pct}}></div>
                        <label className='progress-label'>{`${pct} ${constants.PASSED_VALIDATION}`}</label>
                    </div>
                </div>
            );
            if (itemData.content === '') {
                return;
            }
            // This null check is due to a bug in the library https://github.com/almende/vis/issues/3592
            if (itemData.content.element == null) {
                ReactDOM.render(instance, element);
            }
            return itemData.content;
        }
    };
    return (
        <Box id='timeline-container' data-testid='migration-wave-timeline'>
            <Timeline
                ref={timelineRef}
                options={options}
                initialItems={items}
                initialGroups={groups}>
            </Timeline>
            <div className="timeline-menu">
                <Button variant='icon' iconName='zoom-in' onClick={zoomIn}></Button>
                <Button variant='icon' iconName='zoom-out' onClick={zoomOut}></Button>
                <Button variant='icon' iconName='angle-left' onClick={moveLeft}></Button>
                <Button variant='icon' iconName='angle-right' onClick={moveRight}></Button>
                <Button variant='icon' iconName='angle-left-double' onClick={moveToFirst}></Button>
                <Button variant='icon' iconName='refresh' onClick={moveToCurrent}></Button>
            </div>
        </Box>
    );
}
export default VisTimeline;
